import { mitCode, zeigtCode } from './Auswahlfeld'
import { Betrag } from './Betrag'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { fachlicherSatz } from './Fehlersatz'
import { beschriftung, kontobezeichnung, kurzform, t } from './sprache'
import { eigeneZellen, istStellvertreter, markertext, moeglicheAchsen, vorgabeFuer, weitereVon } from './Aufriss'
import type { Achse, Zelle } from './Aufriss'
import {
  Aufrisszeilen,
  ebenenschluessel,
  Menueknopf,
  menueort,
  Pfeilplatz,
  menuepunkte,
  zeilenpfeilBetaetigen,
  zellkontext,
  zusatzachsen,
} from './Aufrisszeilen'
import type { Achsenquelle, Aufrisszustand, Ebenenstand, Menueruf, Zellkontext } from './Aufrisszeilen'
import { Zeilenmenue } from './Zeilenmenue'
import { zielVon } from './Eigenschaftsfenster'
import type { Eigenschaftsziel } from './Eigenschaftsfenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'
import { alleNull, ohneLeereZeilen, unterdrueckt, useAnzeigeoptionen } from './Anzeigeoptionen'
import { useGliederungsbild } from './Gliederung'
import { Spaltenleiste, useSpaltenbreiten } from './Spaltenbreiten'
import { zeilenId } from './Suche'
import { ausgeschnitten, rufeEinfuegen, schneideAus, umhaengenErlaubt } from './Zwischenablage'
import { loeschenErlaubt, rufeLoeschen } from './Elementloeschung'
import { Bisvermerk, mitBis } from './Bisvermerk'
import type { Bericht as Berichtssatz, Berichtszeile, Schichtangabe } from './typen'

/**
 * Was die Suche der Tabelle mitteilt.
 *
 * **Die Zeile wird zurückhaltend getönt, die getroffene Zahl kräftig.** Beides
 * gleich einzufärben hieße, den Fund in seiner eigenen Zeile suchen zu lassen:
 * Eine Bilanzzeile hat sieben Spalten, und getroffen hat eine.
 */
export type Trefferzustand = {
  /** Je Trefferzeile die Spalten, in denen der Betrag steht — leer bei einem Texttreffer. */
  zellen: Map<string, Set<string>>
  aktiv: string | null
}

/**
 * Ob eine Berichtszeile in keiner ihrer Spalten einen Betrag traegt.
 *
 * Aufgezaehlt und nicht durchsucht: Eine Berichtszeile traegt neben ihren
 * sieben Betraegen auch ihre Ebene, und die ist eine Zahl ueber die
 * Gliederung, keine ueber den Inhalt.
 *
 * Die Schichten und die Stufenspalten zaehlen mit: Eine Zeile, in der NUR eine
 * Stufenspalte steht, traegt etwas und ist nicht leer.
 */
export function istLeereBerichtszeile(z: Berichtszeile): boolean {
  return alleNull(
    z.hb2,
    z.kapKo,
    z.schuKo,
    z.aeKo,
    z.zwErgEUv,
    z.zwErgEAv,
    z.konzern,
    z.schichten,
    z.stufenkonso,
  )
}

/** Der Aufklappzustand, wie ihn der Haken unten liefert — die Bildschirme reichen ihn durch. */
export type Aufrissbedienung = ReturnType<typeof useAufriss>

/**
 * Auf welche Zelle sich ein Wertenachweis bezieht (2.14, R205).
 *
 * **Der ganze Filter reist mit**, die Konsolidierungsspalte eingeschlossen.
 * Ein Doppelklick auf den Konzernwert der Vorräte zeigt die Lieferbelege *und*
 * den Zwischenergebnisbeleg; einer auf die HB-II-Spalte derselben Zeile nur
 * die Lieferbelege. Wer die Spalte nicht mitgibt, beantwortet eine andere
 * Frage als die gestellte.
 */
export type Zellenzeiger = {
  position: string
  positionsname: string
  konto: string | null
  einheit: string | null
  spalte: string
  /**
   * Eine abweichende Periode — im Bild *Perioden* trägt jede Spalte ihre
   * eigene. Ohne sie führte ein Doppelklick auf die Märzspalte zum Nachweis
   * des Dezembers, und der Anwender sähe eine Zahl, die er nicht angeklickt
   * hat. Fehlt sie, gilt die Periode der Kontextzeile.
   */
  periode?: number
  /** Was der Aufriss über Konto und Gesellschaft hinaus festgelegt hat (7.3 Abschnitt 2f). */
  weitere?: Record<string, string | null>
  /** Die Spiegelspalte, aus der die Zelle stammt — nur aus einem Spiegel. */
  spiegelblock?: string
  spiegelspalte?: string
}

export type Nachweisruf = (zeiger: Zellenzeiger) => void

/**
 * Woher eine Wertzelle weiß, wozu sie gehört.
 *
 * **Damit greift der Drill-down überall, ohne dass ein Bildschirm ihn
 * einzeln verdrahtet** (R213): Ein Bericht stellt einmal je Zeile bereit, wo
 * er steht; jedes `<Wert>` darunter nennt nur noch seine Spalte und ist
 * aufrufbar. Ein künftiges Spaltenbild erbt den Nachweis, statt ihn
 * nachzubauen — und wer ihn vergisst, hat eine Zelle ohne Doppelklick, keine
 * mit falschem Filter.
 */
type Zellenlage = {
  ruf: Nachweisruf | null
  position: string
  positionsname: string
  zelle: Zelle
}

const Nachweislage = createContext<Zellenlage | null>(null)

/**
 * Die Lage einer Zeile oder Aufrisszelle, für alle `<Wert>` darunter.
 *
 * Sie zeichnet nichts — sie sagt den Zellen nur, wo sie stehen.
 */
export function Zellen({
  ruf,
  position,
  positionsname,
  zelle,
  children,
}: Zellenlage & { children: React.ReactNode }) {
  const lage = useMemo(
    () => ({ ruf, position, positionsname, zelle }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ruf, position, positionsname, zelle.einheit, zelle.konto, JSON.stringify(zelle.weitere ?? null)],
  )

  return <Nachweislage.Provider value={lage}>{children}</Nachweislage.Provider>
}

/**
 * Die Klasse einer Trefferzeile.
 *
 * Hervorgehoben wird jede, angesprungen die aktive — beides zusätzlich zu dem,
 * was die Statuszeile der Suche in Worten sagt (R143).
 */
export function trefferklasse(treffer: Trefferzustand | undefined, id: string): string {
  if (!treffer?.zellen.has(id)) {
    return ''
  }

  return id === treffer.aktiv ? 'treffer trefferaktiv' : 'treffer'
}

/** Trägt diese Zelle den gefundenen Betrag? */
export function istTrefferzelle(
  treffer: Trefferzustand | undefined,
  id: string,
  spalte: string,
): boolean {
  return treffer?.zellen.get(id)?.has(spalte) ?? false
}

/** Der Name einer Berichtszeile, wie der Nachweis ihn im Kopf nennt. */
export function zeilenname(zeile: {
  code: string
  namen: Record<string, string>
  schluessel?: string
  /** Die Position einer Berichtszeile — ihr Schlüssel, wenn keiner eigens genannt ist. */
  position?: string
  /** Der letzte Monat an dieser Stelle (R1724) — dann mit „· bis MM.JJJJ". */
  giltBis?: number | null
}): string {
  // Ein gerechneter Bericht trägt den Schlüssel als Code (*POS-G-GL*); ohne
  // den Vergleich mit der Position stand er vor dem Namen — am 2026-10-06 im
  // Titel des Anlegefensters, *„Neue Unterposition unter POS-G-GL
  // Gesamtleistung"* (R1237). Der Zusatz steht überall, wo das alte Element
  // erscheint (R1725) — auch im Titel eines Fensters.
  return mitBis(
    mitCode(zeile.code, beschriftung(zeile.namen), ' ', zeile.schluessel ?? positionAus(zeile.position)),
    zeile.giltBis,
  )
}

/**
 * **Die Position hinter einer Zeilenkennung** (7.3 Abschnitt 33, R1723). Im
 * Zeitvergleich steht eine umgehängte Position zweimal, und jede Zeile heißt
 * `Position~Stelle`; wer einen Dienst ruft, braucht die Position.
 */
export function positionAus<T extends string | undefined>(kennung: T): T {
  return (kennung === undefined ? kennung : kennung.split('~')[0]) as T
}

/** Die Stelle hinter einer Zeilenkennung — `undefined` an einer Zeile, die nur an einer Stelle steht. */
export function stelleAus(kennung: string): string | undefined {
  const ab = kennung.indexOf('~')
  return ab < 0 ? undefined : kennung.slice(ab + 1)
}

/**
 * Die Beschriftung einer Berichtszeile: Nummer fett, Name dahinter.
 *
 * **Eine gerechnete Zeile trägt keine Nummer** (R483) — dann steht auch kein
 * leeres `strong` und kein Leerzeichen davor da. Ein Posten des Gesetzes
 * behält seine Nummer auch dann, wenn er eine Gruppe über Buchstabenposten
 * ist; die Unterscheidung trifft der Plan, nicht diese Stelle. Das ist nicht
 * nur ordentlich:
 * Die Positionsspalte lässt sich schmal ziehen (7.3 Abschnitt 10), und ein
 * vorangestelltes Leerzeichen rückte „Gesamtleistung" gegen die Posten
 * darüber ein, die keine Einrückung haben.
 *
 * **An einer Stelle für alle fünf Tabellen**, aus demselben Grund wie `einzug`:
 * Bilanz, GuV und die drei Spiegel bauen dieselbe erste Spalte, und drei
 * Fassungen derselben Regel laufen auseinander, sobald jemand eine anfasst.
 */
export function Zeilenbeschriftung({
  zeile,
}: {
  zeile: { position?: string; code: string; namen: Record<string, string>; giltBis?: number | null }
}) {
  /*
    **Ein vergebener Schlüssel steht nicht vor dem Namen** (R699, R703).

    Seit R676 IST der Schlüssel der Code — `POS-A-II-1` statt `A.II.1` —, und
    die Nummer steht seit R678 in der Bezeichnung selbst: *A.II.1 Grundstücke,
    grundstücksgleiche Rechte …*. Wer den Code davorsetzt, zeigt dieselbe
    Auskunft zweimal, die zweite davon in einer Sprache, die dem Anwender
    nicht gehört.

    `mitCode` kennt die Unterscheidung: Ein **fremder** Code bleibt stehen
    (R679), ein vergebener fällt weg, und ohne Bezeichnung endet die
    Rückfallkette beim Schlüssel (R238).
  */
  const name = beschriftung(zeile.namen)

  // **Ohne Bezeichnung endet die Rückfallkette beim Schlüssel** (R238):
  // Eine leere Zeile sagt nichts, auch nicht über eine neue Position.
  if (!name) {
    return <Namenszeile bis={zeile.giltBis}>{zeile.code}</Namenszeile>
  }

  // **Ein fachlicher Code bleibt und bleibt hervorgehoben** (R483): Die
  // Nummern der Gewinn- und Verlustrechnung — 1., 2., 5.a) — sind die
  // Sprache des Gesetzes und keine Kennung aus unserem Haus.
  const zeigen = zeigtCode(zeile.code, positionAus(zeile.position)) ? zeile.code : ''

  // **Das alte Element sagt, bis wann es galt** (7.3 Abschnitt 33, R1724) —
  // dezent hinter dem Namen, in allen Tabellen dieser Spalte.
  return (
    <Namenszeile bis={zeile.giltBis}>
      {zeigen !== '' && <strong>{zeigen}</strong>}
      {zeigen !== '' && ' '}
      {name}
    </Namenszeile>
  )
}

/**
 * Die Einrückung der ersten Spalte: ein schmaler Rand, dann je Ebene eine Stufe.
 *
 * **An einer Stelle für alle Berichte**, damit Bilanz, GuV, Spiegel und die
 * nachgeladenen Aufrisszeilen dieselbe Treppe steigen — vier Dateien mit
 * derselben Rechnung waren vier Gelegenheiten, sie verschieden zu ändern.
 *
 * Die Stufe ist schmal gehalten (7.3 Abschnitt 10): Der Rand vor dem Code und
 * jede Stufe darunter gehen der Positionsspalte verloren, und die trägt den
 * Wortlaut des § 266 HGB. Eine Bilanz ist vier Ebenen tief; was hier an Stufe
 * gespart wird, steht unten viermal als Text zur Verfügung.
 */
export const EINZUGSRAND = 4
export const EINZUGSSTUFE = 12

/**
 * Die Stufe einer **Aufrissebene** — Konto, Gesellschaft, Partner, Segment,
 * Analysedimension.
 *
 * **Breiter als die Stufe der Gliederung, und das mit Absicht.** Die
 * Gliederungsebenen sind der Bericht; die Aufrissebenen sind der Weg, den
 * einer hineingegangen ist. Wo beide dieselbe Stufe hätten, läse sich ein
 * aufgerissenes Konto wie eine weitere Position des § 266.
 */
export const AUFRISSSTUFE = 16

/** Die Einrückung einer Zeile der Ebene `ebene`, um `zusatz` Pixel weiter. */
export function einzug(ebene: number, zusatz = 0): string {
  return `${EINZUGSRAND + ebene * EINZUGSSTUFE + zusatz}px`
}

/**
 * Ob eine Berichtszeile aufgerissen werden darf.
 *
 * **Kontozeilen entstehen nur an bebuchten Positionen, nie an Summenpositionen**
 * — R165, wörtlich. Eine Summenzeile rechnet sich aus dem, was unter ihr steht,
 * und trägt selbst keine Konten: Sie ist schon beim Zuordnen kein Ziel (R308)
 * und führt nicht ins Journal, sondern auf ihre Summanden (R270).
 *
 * Der Bericht liefert ihr die Beiträge ihrer Summanden trotzdem mit, weil die
 * Gesellschaftsspalten des Konzernaufrisses und die Nullzeilenunterdrückung
 * daraus entstehen (R421). Wer sie aufrisse, sähe **dasselbe Konto zweimal**:
 * einmal unter der Position, auf die es gebucht ist, und einmal unter der
 * Summe darüber, auf die niemand buchen kann.
 *
 * Die Kapitalflussrechnung hielt sich als einzige daran, weil ihre Summenzeilen
 * gar keine Beiträge mitbekommen. Hier steht die Regel für alle fünf.
 *
 * **Seit dem 2026-10-06 geht die Summe selbst auf — nach jeder Achse außer dem
 * Sachkonto** (R1235, 7.3 Abschnitt 28). An der *Gesamtleistung* klappte
 * vorher nichts auf; nach Gesellschaft, Bewertungsebene oder Partner zeigt sie
 * jetzt, woraus sie besteht. Das Konto bleibt gesperrt, aus dem Grund oben.
 */
export function istAufreissbar(zeile: { istSumme: boolean; beitraege?: unknown[] }, moeglich: Achse[]): boolean {
  return zeilenachsen(zeile, moeglich).length > 0
}

/**
 * Die Achsen, nach denen eine Zeile aufgeht (R1235): an einer Summenzeile jede
 * außer dem Sachkonto — und keine, wenn der Bericht ihr keine Beiträge
 * mitgibt (die Summen der Kapitalflussrechnung). Gilt auf jeder Stufe darunter
 * auch: Unter *Gesamtleistung › Holding AG* stünde ein Konto sonst ebenso
 * zweimal.
 */
export function zeilenachsen(zeile: { istSumme: boolean; beitraege?: unknown[] }, moeglich: Achse[]): Achse[] {
  if (!zeile.istSumme) {
    return moeglich
  }

  return (zeile.beitraege?.length ?? 0) === 0 ? [] : moeglich.filter((a) => a !== 'konten')
}

/**
 * Die Klasse, die den **Rang einer Zeile** zeichnet (7.3 Abschnitt 17, R570)
 * — an einer Stelle für alle Tabellen, wie der Einzug.
 *
 * **Der Rang hängt an der Gliederung und nicht am Kennzeichen** (berichtigt am
 * 2026-09-16): Auch eine Zeile ohne Unterpositionen steht in der Gliederung,
 * wenn ihre Geschwister dort stehen. *C. Rechnungsabgrenzungsposten* trägt
 * dieselbe Stufe des § 266 wie *A. Anlagevermögen* und sieht deshalb gleich
 * aus. Gerechnet hat das der Bericht (`rang`); hier wird nur zugeordnet:
 *
 * | Rang | Bild |
 * |---|---|
 * | `ergebnis` | fett, kräftiger Grund, Summenstrich darüber |
 * | `abschnitt` | fett, heller Grund |
 * | `unterste` | **fett, kein Grund** |
 * | *kein Rang* | wie jeder Posten |
 *
 * Die unterste Summe bekommt das Wenigste und nicht etwas anderes: *Ein Rang
 * ist ein Mehr und kein Anderswo* (R485, das in R570 aufgeht). Ein dritter
 * Farbton wäre auf schlechten Bildschirmen und im Ausdruck nicht mehr sicher
 * zu unterscheiden.
 *
 * **Der Rückfall hält die älteren Zeilensätze am Leben:** Die
 * Kapitalflussrechnung liest ihre Zeilen aus dem Zeilenkatalog des Standards
 * und führt gar kein `rang`-Feld — sie bleibt bei zwei Stärken, bis ihr Modell
 * den Rang hergibt (R507).
 *
 * **Eine Summenzeile bleibt nie unhervorgehoben** (R722, 2026-09-24).
 *
 * Bis dahin unterschied diese Stelle zwischen `undefined` („nicht
 * mitgeliefert, leite ab") und `null` („ausdrücklich kein Rang") — und ein
 * neuer Weg, der einen Bericht baut, konnte mit einem einzigen `null` die
 * ganze Gliederung flachlegen. Genau das ist am 2026-09-24 passiert: Der
 * Bericht ohne Lauf (R717) lieferte `rang: null`, und Abschnitt,
 * Zwischensumme und Posten sahen gleich aus.
 *
 * **Der Rang sagt seitdem nur noch, WIE STARK hervorgehoben wird, nicht OB.**
 * Das Ob steht an `istSumme` — einer Eigenschaft des Stammdatums, die jeder
 * Weg mitbringt, weil die Zeile ohne sie gar nicht zu bauen ist. Ein Posten
 * (`istSumme: false`) bleibt unverändert ohne Klasse.
 */
export function rangklasse(zeile: {
  istSumme: boolean
  istErgebnis?: boolean
  rang?: string | null
}): string {
  const rang =
    zeile.rang ??
    (zeile.istSumme ? (zeile.istErgebnis ? 'ergebnis' : 'abschnitt') : null)

  if (rang === null) {
    return ''
  }

  if (rang === 'ergebnis') {
    return 'summe ergebnis'
  }

  return rang === 'unterste' ? 'summe unterste' : 'summe'
}

/**
 * Die erste Zelle einer Berichtszeile: Menüknopf, Pfeil, Code und Name, und
 * der Marker, wenn die Achse von der Vorgabe abweicht.
 *
 * **An einer Stelle für alle Berichte** — Bilanz, GuV, Spiegel und
 * Kapitalflussrechnung zeichnen dieselbe Zeile, und ein Knopf, der nur in
 * einem von vieren steht, wäre der eine, den niemand findet.
 */
export function Zeilenkopf({
  aufreissbar,
  achse,
  vorgabe,
  angebot,
  aufKlappen,
  aufMenue,
  klappbar = false,
  zugeklappt = false,
  aufGliederung,
  children,
}: {
  aufreissbar: boolean
  achse: Achse | undefined
  vorgabe: Achse
  angebot?: Achsenquelle
  aufKlappen: () => void
  aufMenue: (x: number, y: number) => void
  /** Eine Summenposition mit Unterpositionen — sie schiebt sie zusammen (R444). */
  klappbar?: boolean
  zugeklappt?: boolean
  aufGliederung?: () => void
  children: React.ReactNode
}) {
  // **Hat die Zeile Unterpositionen, gehört ihr Pfeil der Gliederung** (R444):
  // Seit dem 2026-10-06 geht auch eine Summe auf (R1235), aber an einer Summe
  // mit Kindern schiebt der Pfeil weiter die Kinder zusammen — ihr Aufriss
  // kommt über das Menü. Eine Formel wie die Gesamtleistung hat keine Kinder;
  // dort öffnet der Pfeil den Aufriss.
  const gliederungspfeil = klappbar && aufGliederung !== undefined
  const aufrisspfeil = aufreissbar && !gliederungspfeil

  // **Knöpfe, Name und Marker in einer Reihe** (7.3 Abschnitt 33, R1724):
  // So bekommt der Name genau den Platz, der übrig bleibt, und eine schmale
  // Spalte kürzt den Namen — nicht den Zusatz „· bis MM.JJJJ" dahinter.
  return (
    <span className="kopfreihe">
      <Menueknopf aufMenue={aufMenue} />
      {aufrisspfeil && (
        <button
          type="button"
          className={`menueknopf ${achse && achse !== vorgabe ? 'abweichend' : ''}`}
          aria-label={t('bericht.aufklappen')}
          aria-expanded={achse !== undefined}
          // Den betätigt auch der Doppelklick auf eine Zahl der Zeile (R1032).
          data-zeilenpfeil=""
          // Linksklick klappt auf und zu, ohne Menü — in der Achse, die für
          // diese Zeile zuletzt galt, sonst der Vorgabe dieser Zeile.
          onClick={aufKlappen}
        >
          {achse ? '▾' : '▸'}
        </button>
      )}
      {/*
        **Derselbe Pfeil an derselben Stelle, und er trifft den anderen nie**
        (R444): Eine Summenzeile hat keinen Aufriss (R165), eine bebuchte
        Position keine Unterpositionen. Die Geste bleibt eine — was sie öffnet,
        sagt die Zeile, an der sie steht.

        **Offen ist die Vorgabe** (R419), deshalb steht hier ▾, bis einer
        zuklappt — umgekehrt zum Aufriss, den erst eine Handlung öffnet.
      */}
      {gliederungspfeil && (
        <button
          type="button"
          className="menueknopf gliederungsknopf"
          aria-label={t('bericht.gliederung.klappen')}
          aria-expanded={!zugeklappt}
          data-zeilenpfeil=""
          onClick={aufGliederung}
        >
          {zugeklappt ? '▸' : '▾'}
        </button>
      )}
      {/* Nichts aufzuklappen: Der Platz bleibt, damit die Ebene bündig steht (R1000). */}
      {!aufrisspfeil && !gliederungspfeil && <Pfeilplatz />}
      {children}
      {achse && achse !== vorgabe && (
        <span className="aufrissmarker">{markertext(achse, angebot?.angebot)}</span>
      )}
    </span>
  )
}

/**
 * **Ein Name, der sich kürzen lässt, und sein Zusatz dahinter** (7.3
 * Abschnitt 33, R1724): Ist die Spalte zu schmal, endet der Name mit „…", und
 * „· bis 09.2026" bleibt stehen. Bis zum 2026-10-14 schnitt die Spalte am Ende
 * ab — genau dort, wo der Zusatz steht.
 */
export function Namenszeile({ bis, children }: { bis?: number | null; children: React.ReactNode }) {
  return (
    <span className="namenszeile">
      <span className="namenstext">{children}</span>
      <Bisvermerk bis={bis} />
    </span>
  )
}

/**
 * Die Unterpositionen einer Position, wie der Bericht sie zeigt — alle Zeilen
 * nach ihr, die tiefer stehen, bis zur nächsten auf ihrer Stufe oder darüber.
 * Die Zeilenfolge ist der Baum (R568). Unter eine eigene Unterposition wird
 * eine Position nicht eingefügt (R1697). Ein Bild ohne Stufen kennt keine;
 * dort weist das Umhängen selbst den Kreis ab (R322).
 */
export function unterpositionen(zeilen: { position: string; ebene?: number }[], position: string): Set<string> {
  const ergebnis = new Set<string>()
  const ab = zeilen.findIndex((z) => z.position === position)
  const stufe = zeilen[ab]?.ebene
  if (stufe === undefined) {
    return ergebnis
  }
  for (const z of zeilen.slice(ab + 1)) {
    if (z.ebene === undefined || z.ebene <= stufe) {
      break
    }
    ergebnis.add(z.position)
  }
  return ergebnis
}

/**
 * Die Punkte eines Berichts-Menüs, mit dem Wertenachweis der angeklickten
 * Zelle — für Bilanz und GuV in allen Spaltenbildern dieselben.
 *
 * Der Nachweis erbt, was die Zelle weiß (R205): Konto und Gesellschaft aus
 * dem Pfad, den Rest des Filters aus dem Aufriss, **die Spalte und die
 * Periode aus der Zelle, auf der das Menü aufging** — so wie der Doppelklick
 * sie erbt. Ohne Zelle, mit der Tastatur geöffnet, gilt der Konzernwert.
 */
export function berichtsmenue({
  menue,
  bericht,
  aufriss,
  quelle,
  aufNachweis,
  aufBuchen,
  aufAnlegen,
  gliederung,
  vorgabeSpalte = 'konzern',
  aufEigenschaften,
  aufReihenfolge,
}: {
  menue: NonNullable<Aufrissbedienung['menue']>
  bericht: {
    zeilen: {
      position: string
      code: string
      namen: Record<string, string>
      istSumme: boolean
      /** Ob Konten an der Position hängen (R495) — die Bedingung für eine Unterposition. */
      hatKonten?: boolean
      /** Ob Unterpositionen darunter hängen (R571) — nicht das Kennzeichen. */
      hatKinder?: boolean
      /** Ob die Zeile ihre Glieder selbst nennt (R481) — dort entsteht kein Konto (R577). */
      istRechenposition?: boolean
      istErgebnis?: boolean
      /** Die Stufe in der Gliederung — daran erkennt das Einfügen die eigenen Unterpositionen (R1697). */
      ebene?: number
      /** Der letzte Monat an dieser Stelle (R1724) — der Titel trägt dann „· bis MM.JJJJ". */
      giltBis?: number | null
      beitraege: Zelle[]
    }[]
  }
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle
  aufNachweis?: Nachweisruf
  /** Buchen aus dem Bericht heraus — die vierte Gruppe des Menüs (R385). */
  aufBuchen?: Nachweisruf
  /**
   * Anlegen aus dem Bericht heraus — die **fünfte Gruppe** (R494).
   *
   * **Ein Schalter je Bericht** (R500): Wer sie nicht durchreicht, hat sie
   * nicht — das Bauteil ist berichtsneutral, eingeschaltet ist es zunächst nur
   * in der Bilanz.
   */
  aufAnlegen?: (art: 'konto' | 'position', position: string, positionsname: string) => void
  /** Die Gliederung des ganzen Berichts (R445) — wo der Bericht eine hat. */
  gliederung?: Parameters<typeof menuepunkte>[0]['gliederung']
  /** Welche Spalte gilt, wenn das Menü nicht auf einer Zelle aufging. */
  vorgabeSpalte?: string
  /**
   * Rechtsklick → Eigenschaften (R858): die Position der Zeile, oder unter
   * einem Aufriss das Konto, die Gesellschaft, der Dimensionswert dort.
   */
  aufEigenschaften?: (ziel: Eigenschaftsziel) => void
  /** Rechtsklick → Reihenfolge ändern (7.4 Abschnitt 32, R1341). */
  aufReihenfolge?: (ziel: Reihenfolgeziel) => void
}) {
  const pfad = pfadVon(menue)
  const letzte = pfad[pfad.length - 1] ?? { einheit: null, konto: null }
  const zeile = bericht.zeilen.find((z) => z.position === menue.position)
  // **Die Position hinter der Zeile** (R1723): Im Zeitvergleich heißt eine
  // umgehängte Position `Position~Stelle`; jeder Dienst bekommt die Position.
  const echt = positionAus(menue.position)
  const vorrat = aufriss.vorratFuer(quelle, zellenVon(bericht, menue.position), menue.position, pfad)
  // **Auf einer Summenzeile jede Achse außer dem Sachkonto** (R1235) — auf
  // jeder Stufe darunter: Bis zum 2026-10-06 bot das Menü dort gar keine an,
  // und an der Gesamtleistung ging nichts auf.
  const achsen = (moeglich: Achse[]) => (zeile ? zeilenachsen(zeile, moeglich) : moeglich)
  // **Formel und Ergebniszeile** (R1234): nur die Aufrisse und die
  // Eigenschaften — kein Nachweis, kein Buchen, kein Anlegen.
  const formel = zeile !== undefined && (zeile.istRechenposition === true || zeile.istErgebnis === true)

  /*
    **Was diese Zeile zulässt** (R495) — entschieden aus dem, was der Bericht
    schon mitbringt, und nicht aus einer Rückfrage: Ein Menü, das erst
    nachlädt, bevor es erscheint, flackert.

    **Zwei Bedingungen, seit dem 2026-09-16 (R577)** — und keine davon fragt
    nach Konten oder Kindern:

    - **Nur auf der Positionszeile selbst** (`pfad.length === 0`). Eine
      Aufrisszeile ist ein Schnitt durch eine Position und keine.
    - **Nichts unter der Ergebniszeile** (R484): Aktiva, Passiva und das
      Konzernjahresergebnis sind die Abschnitte der Rechnung und keine Zeilen
      darin (R321).

    **Was weggefallen ist, und warum** (7.4 Abschnitt 14.13): Hier standen zwei
    weitere Bedingungen — kein Konto an einer Zeile mit Unterpositionen, keine
    Unterposition an einer Zeile mit Konten. Beide kamen aus 2.1 Abschnitt 3.2,
    und diese Schranke ist auf Anwenderwunsch gefallen: Eine Position darf
    Konten und Unterpositionen zugleich tragen, ihr Wert ist die Summe aus
    beidem. **Angeboten wird damit an jeder Positionszeile beides.**

    `hatKonten` und `hatKinder` bleiben an der Berichtszeile stehen: Der
    Aufriss und die Gliederung fragen sie weiter, das Menü nicht mehr.

    **R495 gilt unverändert** — angeboten wird nur, was die Zeile zulässt. Es
    gibt nur zwei Abweisungen weniger, gegen die er zu schützen hätte, und
    `Strukturanlage` hat sie in derselben Änderung aufgegeben. Ein Menü, das
    hier voreilte, böte einen Punkt an, den der Dienst abweist — und genau das
    war der Befund vom Vormittag.
  */
  const anZeile = pfad.length === 0 && zeile !== undefined && zeile.istErgebnis !== true
  // Unter einer Formel entsteht nichts, auch keine Unterposition (R1233,
  // berichtigt R577) — am 2026-10-06 entstand dort *Test* unter der
  // Gesamtleistung, mit einem Wert, der in keine Summe eingegangen wäre.
  const anlegbar = aufAnlegen && anZeile && !formel
  const name = zeile ? zeilenname(zeile) : echt
  // Die Zeile ist ihre Position, eine Aufrisszeile das, was ihre Ebene zeigt (R858).
  const ziel = zeile ? zielVon('position', echt, pfad) : null
  // Der Titel ohne die vergebene Kennung: Sie steht als Code der Zeile, und
  // „POS-A-II-2 A.II.2 Technische Anlagen" sagt nichts, was der Name nicht sagt.
  const eigenschaftsziel =
    ziel && zeile && pfad.length === 0
      ? { ...ziel, titel: zeilenname({ ...zeile, schluessel: echt }) }
      : ziel

  return menuepunkte({
    offen: aufriss.zustand.get(ebenenschluessel(menue.position, pfad)),
    moeglich: achsen(moeglicheAchsen(vorrat, pfad, zusatzachsen(quelle))),
    angebot: quelle?.angebot,
    aufAchse: (a) => aufriss.oeffnen(menue.position, pfad, a),
    aufZuklappen: () => aufriss.zuklappen(menue.position, pfad),
    aufHerkunft:
      aufNachweis && menue.kontext.spalte !== '' && !formel
        ? () => {
            aufriss.setMenue(null)
            aufNachweis({
              position: echt,
              positionsname: zeile ? zeilenname(zeile) : echt,
              konto: letzte.konto,
              einheit: letzte.einheit,
              weitere: weitereVon(pfad),
              spalte: menue.kontext.spalte ?? vorgabeSpalte,
              periode: menue.kontext.periode,
              spiegelblock: menue.kontext.spiegelblock,
              spiegelspalte: menue.kontext.spiegelspalte,
            })
          }
        : undefined,
    bericht: {
      aufAlle: (a) => aufriss.alleAufklappen(bericht.zeilen, a),
      aufZu: aufriss.alleZuklappen,
    },
    gliederung,
    // **Die vierte Gruppe** (R385). Sie erbt denselben Zeiger wie der
    // Wertenachweis — die Zelle weiß, zu welcher Position, welchem Konto,
    // welcher Gesellschaft und welcher Spalte sie gehört (R213).
    //
    // **Nur an einer Kontozeile** (R1232, Entscheidung vom 2026-10-06):
    // *„Buchen kann man nur auf Konten"* — das Konto ist dann vorbelegt, und
    // an einer Position, Summe oder Formel steht der Punkt nicht.
    // Auf ein Stellvertreterkonto wird nicht gebucht (R1624) — es ist kein
    // Konto des Kontenrahmens.
    aufBuchen: aufBuchen && letzte.konto !== null && !istStellvertreter(letzte.konto) && !formel
      ? () => {
          aufriss.setMenue(null)
          aufBuchen({
            position: echt,
            positionsname: zeile ? zeilenname(zeile) : echt,
            konto: letzte.konto,
            einheit: letzte.einheit,
            weitere: weitereVon(pfad),
            spalte: menue.kontext.spalte ?? vorgabeSpalte,
            periode: menue.kontext.periode,
            spiegelblock: menue.kontext.spiegelblock,
            spiegelspalte: menue.kontext.spiegelspalte,
          })
        }
      : undefined,
    // **Die fünfte Gruppe** (R494) — sie nimmt die Position der Zeile und
    // nicht die Zelle: Angelegt wird unter einer Position, und welche Spalte
    // gerade angeklickt war, ändert daran nichts.
    anlage: (() => {
      /*
        **Gelöscht wird, worauf geklickt wurde** (2.1 Abschnitt 3.6, R1705,
        R1707): an einer Kontozeile das Konto, an einer Positionszeile die
        Position — an einer Formel und einer Ergebniszeile nichts. Bis zum
        2026-10-14 gab es in Bilanz und GuV keinen Löschpunkt.
      */
      const loeschen = !loeschenErlaubt() || formel
        ? undefined
        : letzte.konto !== null && !istStellvertreter(letzte.konto)
          ? {
              art: 'konto' as const,
              tun: () => {
                aufriss.setMenue(null)
                rufeLoeschen({ art: 'konto', schluessel: letzte.konto!, name: kontobezeichnung(letzte.konto!) })
              },
            }
          : anZeile
            ? {
                art: 'position' as const,
                tun: () => {
                  aufriss.setMenue(null)
                  rufeLoeschen({ art: 'position', schluessel: echt, name })
                },
              }
            : undefined

      if (!anlegbar && !loeschen) {
        return undefined
      }

      return {
        /*
          **Beides, an jeder Position** (R577) — **an einer Formel nichts**
          (R1233, seit dem 2026-10-06; `anlegbar` oben).

          Der Dienst hat die beiden alten Fragen (Konten? Kinder?) in
          derselben Änderung aufgegeben. Bis zum 2026-10-06 blieb an der
          Rechenposition die Unterposition stehen — sie ändere an der
          Gliedliste nichts. Genau deshalb ging ihr Wert in keine Summe ein,
          und der Auftraggeber hat sie gestrichen: *„Konten und Summenzeilen
          sowie Formeln können keine Elemente unter sich erzeugt bekommen."*
        */
        aufPosition: anlegbar
          ? () => {
              aufriss.setMenue(null)
              aufAnlegen('position', echt, name)
            }
          : undefined,
        aufKonto: anlegbar
          ? () => {
              aufriss.setMenue(null)
              aufAnlegen('konto', echt, name)
            }
          : undefined,
        loeschen,
      }
    })(),
    /*
      **Ausschneiden und Einfügen** (7.3 Abschnitt 31, R1642): Ausgeschnitten
      wird an einer Kontozeile — nicht an einem Stellvertreterkonto, das kein
      Konto des Kontenrahmens ist (R1624) —, eingefügt an einer Positionszeile,
      solange etwas ausgeschnitten ist. An einer Formel und einer Ergebniszeile
      nichts (R1233). In jedem Berichtsbild dasselbe, weil jedes sein Menü hier
      baut.

      **Seit dem 2026-10-14 auch eine Position** (7.3 Abschnitt 32, R1697):
      ausgeschnitten an ihrer Zeile — nicht an einer Formel, einer Ergebniszeile
      und einer Wurzel wie *Aktiva* (`anZeile`) —, eingefügt an jeder anderen
      Positionszeile außer ihren eigenen Unterpositionen.
    */
    ablage: umhaengenErlaubt()
      ? {
          aufAusschneiden:
            letzte.konto !== null && !istStellvertreter(letzte.konto) && !formel
              ? () => {
                  aufriss.setMenue(null)
                  schneideAus('konto', letzte.konto!, kontobezeichnung(letzte.konto!))
                }
              : anZeile && !formel
                ? () => {
                    aufriss.setMenue(null)
                    schneideAus('position', echt, name)
                  }
                : undefined,
          einfuegen: (() => {
            const drin = ausgeschnitten()
            const wohin =
              drin?.art !== 'position' ||
              (drin.schluessel !== echt && !unterpositionen(bericht.zeilen, drin.schluessel).has(menue.position))
            return drin && anZeile && !formel && wohin
              ? {
                  text: t('bericht.menue.einfuegen', { konto: drin.name }),
                  tun: () => {
                    aufriss.setMenue(null)
                    rufeEinfuegen({
                      ...drin,
                      position: echt,
                      positionsname: name,
                      periode: menue.kontext.periode ?? null,
                    })
                  },
                }
              : undefined
          })(),
        }
      : undefined,
    // An der Positionszeile, nicht unter einem Aufriss (R1341).
    aufReihenfolge:
      aufReihenfolge && zeile && pfad.length === 0
        ? () => {
            aufriss.setMenue(null)
            aufReihenfolge({ art: 'positionsgliederung', element: echt, titel: name })
          }
        : undefined,
    aufEigenschaften:
      aufEigenschaften && eigenschaftsziel && !istStellvertreter(letzte.konto)
        ? () => {
            aufriss.setMenue(null)
            aufEigenschaften(eigenschaftsziel)
          }
        : undefined,
  })
}

/**
 * Bilanz oder Gewinn- und Verlustrechnung mit allen Konsolidierungsspalten.
 *
 * Die Spalten sind der eigentliche Punkt: Ein Fachanwender will nicht nur die
 * Konzernzahl sehen, sondern den Weg dorthin. Wer die Zwischenspalten
 * weglässt, spart Platz und nimmt dem Anwender die Prüfbarkeit.
 *
 * **Der Aufriss ist ein Pfad** — siehe `Aufriss.ts`. Linksklick auf den Pfeil
 * klappt auf und zu, ohne Menü; Rechtsklick wählt die Achse.
 */
export function Bericht({
  bericht,
  aufriss,
  quelle,
  treffer,
  aufNachweis,
  aufEigenschaften,
  aufReihenfolge,
  aufBuchen,
  aufAnlegen,
  schichtenOffen,
  aufSchichten,
}: {
  bericht: Berichtssatz
  aufriss: Aufrissbedienung
  /** Woher die Zusatzachsen kommen (7.3 Abschnitt 2f). */
  quelle?: Achsenquelle<Berichtszeile['beitraege'][number]>
  treffer?: Trefferzustand
  /** Doppelklick auf eine Zahl öffnet ihren Wertenachweis (R204). */
  aufNachweis?: Nachweisruf
  /** Rechtsklick → Buchen: die vierte Gruppe des Kontextmenüs (R385). */
  aufBuchen?: Nachweisruf
  /**
   * Rechtsklick → Anlegen: die **fünfte Gruppe** (R494, 7.4 Abschnitt 14).
   *
   * **Der Pilot ist ein Schalter** (R500): Das Bauteil ist berichtsneutral;
   * wer den Ruf nicht durchreicht, hat die Gruppe nicht. Eingeschaltet ist sie
   * zunächst nur in der Bilanz.
   */
  aufAnlegen?: (art: 'konto' | 'position', position: string, positionsname: string) => void
  /**
   * Ob die Handelsbilanz II ihre Schichten zeigt (R402).
   *
   * **Der Zustand kommt von außen, weil er in der Adresse steht** (R156): Ein
   * weitergegebener Verweis zeigt dieselbe Tabelle wie beim Absender. Eine
   * Vorliebe, die nur im Browser des einen liegt, täte das nicht.
   */
  schichtenOffen?: boolean
  aufSchichten?: () => void
  /** Rechtsklick → Eigenschaften (R858). */
  aufEigenschaften?: (ziel: Eigenschaftsziel) => void
  /** Rechtsklick → Reihenfolge ändern (7.4 Abschnitt 32, R1341). */
  aufReihenfolge?: (ziel: Reihenfolgeziel) => void
}) {
  const { menue, setMenue } = aufriss
  const istGuv = bericht.art === 'guv'

  // Die Breiten gehoeren dem Bild: Das Konsolidierungsbild hat andere Spalten
  // als der Konzernaufriss und deshalb seine eigene Einstellung (7.3, Abschnitt 10).
  const { breiten, rahmen } = useSpaltenbreiten(`${bericht.art}:konsolidierung`)

  // **Leere Posten stehen da, bis einer sie wegnimmt** (7.3 Abschnitt 11).
  const optionen = useAnzeigeoptionen()
  const vollstaendig = unterdrueckt(optionen, 'positionen')
    ? ohneLeereZeilen(bericht.zeilen, istLeereBerichtszeile)
    : bericht.zeilen

  // **Und was einer zusammengeschoben hat, bleibt zusammengeschoben**
  // (7.3 Abschnitt 13, R444). Nach der Nullzeilenunterdrückung gerechnet: Der
  // Pfeil gehört an das, was die Tabelle wirklich zeigt.
  const gliederung = useGliederungsbild(vollstaendig)
  const zeilen = gliederung.zeilen

  // Aufklappbar nur, wenn der Lauf Schichten kennt. Ein Lauf von vor der
  // Schichtung hat keine — dann steht dort die Spalte, die immer dort stand,
  // und kein Pfeil, der nichts zu zeigen hätte.
  const schichten = bericht.schichten ?? []

  const offen = schichtenOffen === true && schichten.length > 0

  /*
    **Die gelieferte Sicht hat eine Wertspalte** (R753). Keine
    Handelsbilanz II, keine Konsolidierungsschritte, keine Schichtenachse:
    Was hier steht, ist addiert und nicht konsolidiert — und wo keine
    Konsolidierungsspalte steht, sieht man das, ohne es zu lesen.
  */
  const geliefert = bericht.herkunft === 'geliefert'

  /*
    **Die Endspalte trägt den Namen ihrer Bewertungsebene** (R777).

    Sie ist die Summe der Gruppe — im gelieferten Bericht die der Schichten, im
    gerechneten zusätzlich die der Konsolidierungsspalten. Konsolidierungs-
    sachverhalte sind Typen derselben Dimension und werden in derselben Gruppe
    aggregiert (Auftraggeber, 2026-09-26).

    **Bis dahin standen dort feste Texte** — *Geliefert* in der einen Sicht,
    *Konzern* in der anderen. Beide sagten nichts über die Bewertungsebene, für
    die gerechnet wurde, und ein umbenanntes Stammdatum kam nie an.
  */
  const gruppenname = bericht.gruppe
    ? kurzform(bericht.gruppe.kuerzel, bericht.gruppe.namen) || bericht.gruppe.code
    : t(geliefert ? 'spalte.geliefert' : 'spalte.konzern')

  /*
    **Die Schichten stehen auch hier** (R759). Die eine Wertspalte betrifft
    die Konsolidierungsschritte — die gibt es ohne Lauf nicht. *Woraus* eine
    Zahl besteht, ist davon unberührt: Die Bewertungsebene ist eine Gruppe aus
    gelieferten Rohdaten und den Anpassungen darüber, und gerade bei einer
    einzelnen Gesellschaft ist das die Frage.
  */
  const spaltenzahl = (geliefert ? 2 : 8) + (offen ? schichten.length : 0)

  const aufMenue: Menueruf = (x, y, position, eltern, zelle, kontext) =>
    setMenue({ x, y, position, eltern, zelle, kontext })

  return (
    <div className="tabellenrahmen" ref={rahmen}>
      <table className="positionstabelle">
        <Spaltenleiste breiten={breiten} spalten={spaltenzahl} />
        <thead>
          <tr>
            {/*
              **Der Berichtsname steht in der Tabelle**, wo sonst „Position"
              stand (R411). Das Wort erklärte die Spalte, deren Inhalt sich von
              selbst erklärt — und die Überschrift darüber kostete eine Zeile
              Bildschirm, die die Tabelle besser gebraucht.
            */}
            <th className="positionsspalte">
              {t(istGuv ? 'bereich.berichte.guv' : 'bereich.berichte.bilanz')}
            </th>

            {/*
              Aufgeklappt stehen die Schichten VOR ihrer Summe — man liest von
              den Rohdaten über die Anpassung zur Handelsbilanz II, in der
              Richtung, in der sie entsteht (R402).
            */}
            {offen &&
              schichten.map((s) => (
                <th key={s.schluessel} className="zahl schichtspalte" title={beschriftung(s.namen)}>
                  {kurzform(s.kuerzel, s.namen) || s.code}
                </th>
              ))}

            {/*
              **Eingefärbt nur, solange sie aufgeklappt ist.** Dann ist die
              Handelsbilanz II die Summe der Spalten links von ihr, und der Ton
              sagt genau das. Zugeklappt ist sie eine Angabe unter anderen —
              links steht nichts, was sie zusammenzählte, und eine Farbe wäre
              dort eine Behauptung.
            */}
            {!geliefert && (
            <>
            <th className={offen ? 'zahl summenspalte' : 'zahl'}>
              {schichten.length > 0 && aufSchichten ? (
                <button
                  type="button"
                  className="spaltenklapper"
                  aria-expanded={offen}
                  title={t(offen ? 'spalte.hb2.zuklappen' : 'spalte.hb2.aufklappen')}
                  onClick={aufSchichten}
                >
                  {t('spalte.hb2')} <span aria-hidden="true">{offen ? '▾' : '▸'}</span>
                </button>
              ) : (
                t('spalte.hb2')
              )}
            </th>
            <th className="zahl">{t('spalte.kapko')}</th>
            <th className="zahl">{t('spalte.schuko')}</th>
            <th className="zahl">{t('spalte.aeko')}</th>
            <th className="zahl">{t('spalte.zwerge_uv')}</th>
            <th className="zahl">{t('spalte.zwerge_av')}</th>
            </>
            )}
            {/*
              **In der gelieferten Sicht klappt die Endspalte auf** (R759).
              Sie ist dort die Summe ihrer Schichten — die Spalte der
              Handelsbilanz II, an der der Pfeil sonst hängt, gibt es hier
              nicht, weil zwischen beiden kein Konsolidierungsschritt liegt
              (R466).
            */}
            <th className={geliefert && offen ? 'zahl konzern summenspalte' : 'zahl konzern'}>
              {geliefert && schichten.length > 0 && aufSchichten ? (
                <button
                  type="button"
                  className="spaltenklapper"
                  aria-expanded={offen}
                  title={t(offen ? 'spalte.hb2.zuklappen' : 'spalte.hb2.aufklappen')}
                  onClick={aufSchichten}
                >
                  {gruppenname} <span aria-hidden="true">{offen ? '▾' : '▸'}</span>
                </button>
              ) : (
                gruppenname
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((zeile) => (
            <Zeilenblock
              key={zeile.position}
              zeile={zeile}
              aufriss={aufriss}
              quelle={quelle}
              treffer={treffer}
              aufNachweis={aufNachweis ?? null}
              aufMenue={aufMenue}
              schichten={offen ? schichten : []}
              geliefert={geliefert}
              spaltenzahl={spaltenzahl}
              klappbar={gliederung.klappbar(zeile.position)}
              zugeklappt={gliederung.zugeklappt(zeile.position)}
              aufGliederung={() => gliederung.umschalten(zeile.position)}
            />
          ))}
          {zeilen.length === 0 && (
            <tr>
              <td colSpan={spaltenzahl} className="hinweis">
                {t(istGuv ? 'bericht.leer.guv' : 'bericht.leer.bilanz')}
              </td>
            </tr>
          )}
        </tbody>

        {/*
          **Der Bericht nennt, was er nicht zeigen kann** (R755).

          Ein Konto, das an keiner Position hängt, hat keine Zeile — der
          Bericht bleibt in sich schlüssig und ist nur um diesen Betrag zu
          klein. Genau deshalb fällt es niemandem auf: Am 2026-09-25 trug ein
          Reisekostenkonto drei Monate lang 3.200 im Monat, die nirgends
          standen.

          **Unter der Tabelle und nicht darüber:** Wer den Bericht liest,
          sucht seine Zahl; der Hinweis gehört dorthin, wo er ankommt, wenn
          sie nicht stimmt. Und er steht nur da, wenn es etwas zu sagen gibt.
        */}
        {bericht.ohneStelle && bericht.ohneStelle.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={spaltenzahl} className="hinweis">
                {t('bericht.ohnestelle')}{' '}
                {bericht.ohneStelle.map((luecke, i) => (
                  <span key={luecke.konto}>
                    {i > 0 && ', '}
                    {/*
                      **Der Schlüssel steht nicht vor dem Namen** (R703). Seit die
                      Bezeichnung die Nummer trägt (R678), ist beides dasselbe:
                      „KTO-6250 6250 Reisekosten" sagt zweimal, was einmal reicht.
                      Ein Kontenrahmen mit eigener Nummer zeigt sie weiterhin —
                      dieselbe Entscheidung wie überall sonst (R699).
                    */}
                    {zeigtCode(luecke.nummer, luecke.konto) && (
                      <>
                        <strong>{luecke.nummer}</strong>{' '}
                      </>
                    )}
                    {beschriftung(luecke.namen)} <Betrag wert={luecke.betrag} />
                  </span>
                ))}
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={berichtsmenue({
            menue,
            aufEigenschaften,
            aufReihenfolge,
            bericht,
            aufriss,
            quelle,
            aufNachweis,
            aufBuchen,
            aufAnlegen,
            gliederung: gliederung.menue,
          })}
          aufSchliessen={() => setMenue(null)}
        />
      )}
    </div>
  )
}

/**
 * Die sieben Wertzellen einer Zeile oder Aufrisszelle des Konsolidierungsbilds
 * — und, aufgeklappt, die Schichten vor der Handelsbilanz II (R402).
 */
function Konsolidierungsspalten({
  z,
  id,
  treffer,
  schichten,
  geliefert,
}: {
  z: {
    hb2: number
    kapKo: number
    schuKo: number
    aeKo: number
    zwErgEUv: number
    zwErgEAv: number
    konzern: number
    schichten?: number[]
  }
  id: string
  treffer?: Trefferzustand
  /** Die Schichten des Kopfes. Leer heißt zugeklappt. */
  schichten?: Schichtangabe[]
  /** In der gelieferten Sicht steht nur die eine Wertspalte (R753). */
  geliefert?: boolean
}) {
  if (geliefert) {
    /*
      **Die Schichten stehen vor der Endspalte** (R759) — in der Richtung, in
      der die Zahl entsteht: von den gelieferten Rohdaten über die Anpassung
      zum ausgewiesenen Wert.

      Die Konsolidierungsspalten dazwischen entfallen (R753), nicht die
      Herkunft: Was hier steht, ist addiert und nicht konsolidiert, aber es
      besteht trotzdem aus Teilen.
    */
    return (
      <>
        {(schichten ?? []).map((s, i) => (
          <Wert
            key={s.schluessel}
            wert={z.schichten?.[i] ?? 0}
            klasse="schichtspalte"
            spalte={'hb2:' + s.schluessel}
            fund={istTrefferzelle(treffer, id, 'konzern')}
          />
        ))}
        <Wert
          wert={z.konzern}
          klasse={(schichten ?? []).length > 0 ? 'konzern summenspalte' : 'konzern'}
          spalte="konzern"
          fund={istTrefferzelle(treffer, id, 'konzern')}
        />
      </>
    )
  }

  return (
    <>
      {/*
        Die Schicht trägt ihre eigene Spaltenkennung in den Wertenachweis:
        „hb2:LDG-ROH" heißt dort die Handelsbilanz II, eingegrenzt auf das, was
        aus den Rohdaten kam. Ein Doppelklick beantwortet damit nicht mehr nur
        „woraus besteht diese Zahl", sondern „woraus besteht dieser Teil davon".
      */}
      {(schichten ?? []).map((s, i) => (
        <Wert
          key={s.schluessel}
          wert={z.schichten?.[i] ?? 0}
          klasse="schichtspalte"
          spalte={'hb2:' + s.schluessel}
          fund={istTrefferzelle(treffer, id, 'hb2')}
        />
      ))}

      <Wert
        wert={z.hb2}
        klasse={(schichten ?? []).length > 0 ? 'summenspalte' : undefined}
        spalte="hb2"
        fund={istTrefferzelle(treffer, id, 'hb2')}
      />
      <Wert wert={z.kapKo} spalte="kapko" fund={istTrefferzelle(treffer, id, 'kapko')} />
      <Wert wert={z.schuKo} spalte="schuko" fund={istTrefferzelle(treffer, id, 'schuko')} />
      <Wert wert={z.aeKo} spalte="aeko" fund={istTrefferzelle(treffer, id, 'aeko')} />
      <Wert wert={z.zwErgEUv} spalte="zwerge_uv" fund={istTrefferzelle(treffer, id, 'zwerge_uv')} />
      <Wert wert={z.zwErgEAv} spalte="zwerge_av" fund={istTrefferzelle(treffer, id, 'zwerge_av')} />
      <Wert wert={z.konzern} klasse="konzern" spalte="konzern" fund={istTrefferzelle(treffer, id, 'konzern')} />
    </>
  )
}

function Zeilenblock({
  zeile,
  aufriss,
  quelle,
  treffer,
  aufNachweis,
  aufMenue,
  schichten,
  geliefert,
  spaltenzahl,
  klappbar = false,
  zugeklappt = false,
  aufGliederung,
}: {
  zeile: Berichtszeile
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle<Berichtszeile['beitraege'][number]>
  treffer?: Trefferzustand
  aufNachweis: Nachweisruf | null
  aufMenue: Menueruf
  /** In der gelieferten Sicht steht nur eine Wertspalte (R753). */
  geliefert?: boolean
  /** Die aufgeklappten Schichten des Kopfes (R402) — leer heißt zugeklappt. */
  schichten?: Schichtangabe[]
  spaltenzahl?: number
  /** Die Gliederung dieser Zeile (R444) — Pfeil nur, wo etwas darunter steht. */
  klappbar?: boolean
  zugeklappt?: boolean
  aufGliederung?: () => void
}) {
  const achse = aufriss.zustand.get(ebenenschluessel(zeile.position, []))
  const vorgabe = vorgabeFuer(zeile.beitraege)
  const moeglich = zeilenachsen(zeile, moeglicheAchsen(zeile.beitraege, [], zusatzachsen(quelle)))
  const aufreissbar = istAufreissbar(zeile, moeglich)
  const leer: Zelle = { einheit: null, konto: null }
  const id = zeilenId(zeile.position, [])
  const name = zeilenname(zeile)
  const eigene = eigeneZellen(zeile)

  return (
    <>
      <tr
        id={id}
        className={[
          rangklasse(zeile),
          aufreissbar ? 'aufklappbar' : '',
          // Keine Dämpfung für eine Zeile ohne Betrag (R867, 7.3 Abschnitt 21):
          // Das leere Wertfeld sagt es, und jede Zeile steht in der Farbe ihrer Ebene.
          trefferklasse(treffer, id),
        ]
          .join(' ')
          .trim()}
        tabIndex={0}
        onContextMenu={(e) => {
          e.preventDefault()
          const ort = menueort(e, e.currentTarget)
          aufMenue(ort.x, ort.y, zeile.position, [], leer, zellkontext(e))
        }}
      >
        <Zellen ruf={aufNachweis} position={zeile.position} positionsname={name} zelle={leer}>
          <td style={{ paddingLeft: einzug(zeile.ebene) }}>
            <Zeilenkopf
              aufreissbar={aufreissbar}
              achse={achse}
              vorgabe={vorgabe}
              angebot={quelle}
              aufKlappen={() => aufriss.umschalten(zeile.position, [], achse ?? moeglich[0] ?? vorgabe)}
              aufMenue={(x, y) => aufMenue(x, y, zeile.position, [], leer, {})}
              klappbar={klappbar}
              zugeklappt={zugeklappt}
              aufGliederung={aufGliederung}
            >
              <Zeilenbeschriftung zeile={zeile} />
            </Zeilenkopf>
          </td>
          <Konsolidierungsspalten z={zeile} id={id} treffer={treffer} schichten={schichten} geliefert={geliefert} />
        </Zellen>
      </tr>

      {/*
        Nur unter einer Zeile, die aufgerissen werden darf — eine Summenzeile
        seit dem 2026-10-06 auch, nur nicht nach Sachkonten (R1235). Der
        Aufklappzustand überlebt den Wechsel von Periode und Bericht, und was
        dort eine Position mit Konten war, kann hier eine Summe sein; das fängt
        `summe` in den Aufrisszeilen.
      */}
      {aufreissbar && (
        <Aufrisszeilen
          position={zeile.position}
          summe={zeile.istSumme}
          ebeneTiefe={zeile.ebene}
          zellen={zeile.beitraege}
          eltern={[]}
          aufriss={aufriss}
          quelle={quelle}
          vorgabe={vorgabe}
          spaltenzahl={spaltenzahl ?? 8}
          treffer={treffer}
          aufMenue={aufMenue}
          // Dieselben Spalten wie in der Zeile darüber: Erst damit ist zu
          // sehen, welcher Saldo auf welchem Konto die Positionssumme
          // zusammensetzt — und in welcher Spalte er eliminiert wird.
          werteSpalten={(z, kennung) => (
            <Zellen ruf={aufNachweis} position={zeile.position} positionsname={name} zelle={z}>
              <Konsolidierungsspalten z={z} id={kennung} treffer={treffer} schichten={schichten} geliefert={geliefert} />
            </Zellen>
          )}
        />
      )}

      {/*
        **Die eigenen Konten einer Position mit Unterpositionen** (7.3
        Abschnitt 30, R1621): vor den Unterpositionen, solange die Gliederung
        offen ist. Bis zum 2026-10-12 standen sie nirgends — unter C.II. fehlten
        die 3,3 Mio. auf Konto 3100. Der Aufriss nach Sachkonto an der Zeile
        selbst bleibt gesperrt (R1235): Er zeigte die Konten der
        Unterpositionen ein zweites Mal.
      */}
      {!zugeklappt && eigene.length > 0 && (
        <Aufrisszeilen
          position={zeile.position}
          fest="konten"
          ebeneTiefe={zeile.ebene}
          zellen={eigene}
          eltern={[]}
          aufriss={aufriss}
          quelle={quelle}
          vorgabe={vorgabe}
          spaltenzahl={spaltenzahl ?? 8}
          treffer={treffer}
          aufMenue={aufMenue}
          werteSpalten={(z, kennung) => (
            <Zellen ruf={aufNachweis} position={zeile.position} positionsname={name} zelle={z}>
              <Konsolidierungsspalten z={z} id={kennung} treffer={treffer} schichten={schichten} geliefert={geliefert} />
            </Zellen>
          )}
        />
      )}
    </>
  )
}

/** Der Schlüssel einer nachgeladenen Ebene im Zwischenspeicher. */
function ladeschluessel(quelle: Achsenquelle, position: string, pfad: Zelle[], achse: Achse): string {
  return `${quelle.kennung}#${ebenenschluessel(position, pfad)}#${achse}`
}

/**
 * Der Aufklappzustand aller Zeilen eines Berichts.
 *
 * Als eigener Haken, weil ihn alle Spaltenbilder gleich brauchen — und
 * weil „umschalten" mehr ist, als es aussieht: Wer eine Ebene schließt, muss
 * auch alles darunter schließen, sonst klappt sie beim nächsten Öffnen mit
 * einem Zustand auf, den niemand gesetzt hat.
 *
 * **Und die nachgeladenen Ebenen wohnen hier** (7.3 Abschnitt 2f): Was einmal
 * geholt ist, bleibt, solange der Bericht steht — wer eine Kostenstelle zu-
 * und wieder aufklappt, wartet nicht zweimal. Der Schlüssel trägt den
 * Bericht und den Lauf; ein anderer Lauf ist ein anderer Vorrat.
 */
export function useAufriss() {
  const [zustand, setZustand] = useState<Aufrisszustand>(new Map())
  const [geladen, setGeladen] = useState<Map<string, Ebenenstand>>(new Map())
  const angefordert = useRef<Set<string>>(new Set())
  const [menue, setMenue] = useState<{
    x: number
    y: number
    position: string
    eltern: Zelle[]
    zelle: Zelle
    kontext: Zellkontext
  } | null>(null)

  function setzen(aendern: (neu: Aufrisszustand) => void) {
    setZustand((vorher) => {
      const neu = new Map(vorher)
      aendern(neu)
      return neu
    })
  }

  function umschalten(position: string, eltern: Zelle[], achse: Achse) {
    const schluessel = ebenenschluessel(position, eltern)
    setzen((neu) => {
      if (neu.get(schluessel) === achse) {
        entferneAbSchluessel(neu, schluessel)
      } else {
        neu.set(schluessel, achse)
      }
    })
  }

  /**
   * Eine Ebene auf eine Achse stellen — ohne Umschalten. Das Menü wählt eine
   * Achse; wer die schon gewählte noch einmal wählt, will sie nicht schließen.
   * Steht die Ebene auf einer anderen Achse, wird alles darunter geschlossen:
   * Es gehörte zu einem anderen Pfad.
   */
  function oeffnen(position: string, eltern: Zelle[], achse: Achse) {
    const schluessel = ebenenschluessel(position, eltern)
    setzen((neu) => {
      if (neu.get(schluessel) !== achse) {
        entferneAbSchluessel(neu, schluessel)
        neu.set(schluessel, achse)
      }
    })
  }

  /**
   * Einen ganzen Pfad öffnen — was ein Suchtreffer braucht.
   *
   * Die Achse jeder Stufe ergibt sich aus dem Pfad selbst: Was neu gefüllt
   * ist, wurde dort aufgerissen. Eine Ebene, die der Anwender anders geöffnet
   * hatte, wird dabei umgestellt — der Sprung zeigt den Treffer, und dafür
   * muss der Weg gelten, auf dem die Suche ihn gefunden hat.
   */
  function pfadOeffnen(position: string, pfad: Zelle[]) {
    setzen((neu) => {
      let bisher: Zelle[] = []
      for (const zelle of pfad) {
        const schonKonto = bisher.some((z) => z.konto !== null)
        neu.set(
          ebenenschluessel(position, bisher),
          zelle.konto !== null && !schonKonto ? 'konten' : 'einheiten',
        )
        bisher = [...bisher, zelle]
      }
    })
  }

  function zuklappen(position: string, eltern: Zelle[]) {
    setzen((neu) => entferneAbSchluessel(neu, ebenenschluessel(position, eltern)))
  }

  function alleAufklappen(
    zeilen: { position: string; istSumme?: boolean; beitraege: Zelle[] }[],
    achse: Achse,
  ) {
    setzen((neu) => {
      neu.clear()
      for (const z of zeilen) {
        // „Alles aufklappen" klappt auf, was einer einzeln aufklappen könnte,
        // und nichts darüber hinaus: eine Summenzeile seit dem 2026-10-06
        // auch (R1235), nur nicht nach Sachkonten.
        if (!zeilenachsen({ istSumme: z.istSumme ?? false, beitraege: z.beitraege }, [achse]).includes(achse)) {
          continue
        }

        if (z.beitraege.some((b) => (achse === 'konten' ? b.konto : b.einheit) !== null)) {
          neu.set(ebenenschluessel(z.position, []), achse)
        }
      }
    })
  }

  /** Der Stand einer nachgeladenen Ebene — „lädt", solange sie nicht angefordert oder noch unterwegs ist. */
  const ebeneVon = useCallback(
    <T extends Zelle>(quelle: Achsenquelle<T>, position: string, pfad: Zelle[], achse: Achse): Ebenenstand<T> =>
      (geladen.get(ladeschluessel(quelle, position, pfad, achse)) as Ebenenstand<T> | undefined) ?? {
        zustand: 'laedt',
      },
    [geladen],
  )

  /**
   * Eine Ebene holen — genau einmal, auch wenn sie mehrfach gezeichnet wird.
   *
   * Die Merkliste der laufenden Abrufe liegt in einem Ref und nicht im
   * Zustand: Zwei Zeichnungen derselben Ebene im selben Augenblick müssen
   * **synchron** sehen, dass der Abruf schon unterwegs ist.
   */
  const anfordern = useCallback(
    (quelle: Achsenquelle, position: string, pfad: Zelle[], achse: Achse) => {
      const schluessel = ladeschluessel(quelle, position, pfad, achse)
      if (angefordert.current.has(schluessel)) {
        return
      }

      angefordert.current.add(schluessel)
      setGeladen((vorher) => new Map(vorher).set(schluessel, { zustand: 'laedt' }))

      quelle
        .lade(position, pfad, achse)
        .then((zellen) =>
          setGeladen((vorher) => new Map(vorher).set(schluessel, { zustand: 'bereit', zellen })),
        )
        .catch((f) => {
          // Ein Fehler darf noch einmal versucht werden — beim nächsten Aufklappen.
          angefordert.current.delete(schluessel)
          // Am Ort nur der fachliche Satz; eine Störung meldet das Störungsfenster (R1003/R1004).
          const satz = fachlicherSatz(f)
          setGeladen((vorher) => new Map(vorher).set(schluessel, { zustand: 'fehler', satz }))
        })
    },
    [],
  )

  /**
   * Der Zellenvorrat, aus dem die Ebene unter einem Pfad geschnitten wird:
   * die Zellen des Berichts — oder, sobald der Pfad eine nachgeladene Zelle
   * enthält, die Zellen jenes Abrufs samt Kreuzzellen.
   */
  const vorratFuer = useCallback(
    <T extends Zelle>(quelle: Achsenquelle<T> | undefined, zellen: T[], position: string, pfad: Zelle[]): T[] => {
      if (!quelle) {
        return zellen
      }

      // Die letzte nachgeladene Ebene auf dem Pfad ist der Vorrat.
      for (let i = pfad.length; i > 0; i--) {
        const eltern = pfad.slice(0, i - 1)
        const achse = zustand.get(ebenenschluessel(position, eltern))
        if (achse && achse !== 'konten' && achse !== 'einheiten') {
          const stand = geladen.get(ladeschluessel(quelle, position, eltern, achse)) as Ebenenstand<T> | undefined
          return stand?.zustand === 'bereit' ? stand.zellen : []
        }
      }

      return zellen
    },
    [zustand, geladen],
  )

  return {
    zustand,
    menue,
    setMenue,
    umschalten,
    oeffnen,
    pfadOeffnen,
    zuklappen,
    alleAufklappen,
    alleZuklappen: () => setZustand(new Map()),
    ebeneVon,
    anfordern,
    vorratFuer,
  }
}

/** Schließt eine Ebene und alles, was darunter offen war. */
function entferneAbSchluessel(zustand: Aufrisszustand, schluessel: string): Aufrisszustand {
  for (const k of [...zustand.keys()]) {
    if (k === schluessel || k.startsWith(`${schluessel}>`)) {
      zustand.delete(k)
    }
  }

  return zustand
}

/** Der Pfad, auf den sich ein geöffnetes Menü bezieht. */
export function pfadVon(menue: { eltern: Zelle[]; zelle: Zelle }): Zelle[] {
  return menue.zelle.einheit === null && menue.zelle.konto === null && !menue.zelle.weitere
    ? []
    : [...menue.eltern, menue.zelle]
}

/** Die Zellen der Zeile, auf die sich ein geöffnetes Menü bezieht. */
export function zellenVon<T extends { zeilen: { position: string; beitraege: Zelle[] }[] }>(
  bericht: T,
  position: string,
): Zelle[] {
  return bericht.zeilen.find((z) => z.position === position)?.beitraege ?? []
}

/**
 * Null bleibt leer statt „0,00" — sonst liest sich die Tabelle als Zahlenteppich.
 *
 * **Doppelklick auf einen Kontowert öffnet den Wertenachweis** (R204), **auf
 * jeden anderen Wert klappt er die Zeile auf und zu** (7.3 Abschnitt 27,
 * R1032) — eine Position, auf jeder Stufe, und eine Aufrisszeile ohne Konto.
 * Kontowert ist, was ein Konto trägt, auch eine Gesellschaft unter einem
 * Konto. Die Eingabetaste auf der fokussierten Zelle tut dasselbe: Keine
 * Funktion kennt nur die Maus (R143). Beides nur, wo etwas steht — eine leere
 * Zelle hat keine Herkunft, und ein Fokusstopp auf ihr wäre ein Versprechen,
 * das beim Drücken bricht.
 *
 * **Die Zelle sagt, wo sie steht** — Spalte, Periode, Spiegelspalte stehen
 * als Datenattribute an ihr. Das Kontextmenü liest sie von dort, und der
 * Nachweis aus dem Menü ist derselbe wie der aus dem Doppelklick (R213).
 */
function Wert({
  wert,
  klasse,
  fund,
  spalte,
  zelle,
  periode,
  spiegel,
}: {
  wert: number
  klasse?: string
  /** Diese Zelle trägt den gesuchten Betrag. */
  fund?: boolean
  /** Welche Konsolidierungsspalte hier steht — der halbe Filter des Nachweises. */
  spalte?: string
  /**
   * Eine abweichende Zelle: im Bild *Konzernstruktur* trägt jede Spalte eine
   * Gesellschaft, und die Zelle ist dort eine andere als die der Zeile.
   */
  zelle?: Zelle
  /** Eine abweichende Periode: im Bild *Perioden* trägt jede Spalte ihre eigene. */
  periode?: number
  /** Die Spiegelspalte, aus der die Zelle stammt — nur in einem Spiegel. */
  spiegel?: { block: string; spalte: string }
}) {
  const lage = useContext(Nachweislage)
  const kontowert = (zelle ?? lage?.zelle)?.konto != null
  const aufrufbar = kontowert && lage?.ruf != null && spalte !== undefined && wert !== 0
  const klappbar = !kontowert && lage != null && wert !== 0

  const klassen = [
    'zahl',
    klasse ?? '',
    wert < 0 ? 'minus' : '',
    fund ? 'trefferzelle' : '',
    aufrufbar || klappbar ? 'nachweisbar' : '',
  ]
    .join(' ')
    .trim()

  function rufen(td: HTMLElement) {
    if (klappbar) {
      zeilenpfeilBetaetigen(td)
      return
    }

    if (!aufrufbar || !lage?.ruf) {
      return
    }

    const gemeint = zelle ?? lage.zelle
    lage.ruf({
      position: lage.position,
      positionsname: lage.positionsname,
      konto: gemeint.konto,
      einheit: gemeint.einheit,
      weitere: gemeint.weitere,
      spalte: spalte!,
      periode,
      spiegelblock: spiegel?.block,
      spiegelspalte: spiegel?.spalte,
    })
  }

  return (
    <td
      className={klassen}
      tabIndex={aufrufbar || klappbar ? 0 : undefined}
      title={aufrufbar ? t('nachweis.menue') : klappbar ? t('bericht.aufklappen') : undefined}
      // Die Zelle nennt ihre Spalte — für das Menü, das auf ihr aufgeht.
      // Ohne Spalte gibt es keinen Nachweis, und das steht dann auch da:
      // eine leere Kennung, damit das Menü ihn nicht mit dem Konzernwert
      // verwechselt.
      data-spalte={spalte ?? ''}
      data-periode={periode}
      // Der Betrag, der dasteht — zum Markieren und Summieren (R1016).
      data-wert={wert}
      data-spiegelblock={spiegel?.block}
      data-spiegelspalte={spiegel?.spalte}
      onDoubleClick={(e) => rufen(e.currentTarget)}
      // Ein Doppelklick markiert im Browser das Wort darunter. Hier öffnet
      // er den Nachweis oder klappt auf, und die Zahl soll danach nicht blau
      // stehen bleiben — der zweite Klick wird deshalb nicht als Auswahl
      // gewertet. Der erste bleibt, damit sich die Zahl weiterhin kopieren lässt.
      onMouseDown={(e) => {
        if ((aufrufbar || klappbar) && e.detail > 1) {
          e.preventDefault()
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          rufen(e.currentTarget)
        }
      }}
    >
      <Betrag wert={wert} leerBeiNull />
    </td>
  )
}

export { Wert }
